import { zodResolver } from '@hookform/resolvers/zod';
import { Pencil, Plus, Tags, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { DuplicateCategoryError } from '@/features/categories/repository';
import { useCategoriesStore } from '@/features/categories/store';
import { useLedgerStore } from '@/features/ledger/store';
import {
  Badge,
  Button,
  CATEGORY_ICON_KEYS,
  Card,
  CategoryGlyph,
  ColorPicker,
  EmptyState,
  IconButton,
  IconPicker,
  Input,
  Modal,
  PageHeader,
  SWATCH_COLORS,
  confirm,
  toast,
} from '@/ui';
import type { Category } from '@domain/types';

const categorySchema = z.object({
  name: z.string().trim().min(1, 'Enter a name.').max(40, 'Keep it under 40 characters.'),
  icon: z.string().min(1),
  color: z.string().min(1),
});

type CategoryForm = z.infer<typeof categorySchema>;

export function CategoriesPage() {
  const categories = useCategoriesStore((state) => state.categories);
  const create = useCategoriesStore((state) => state.create);
  const update = useCategoriesStore((state) => state.update);
  const remove = useCategoriesStore((state) => state.remove);
  const usage = useCategoriesStore((state) => state.usage);
  const refreshLedger = useLedgerStore((state) => state.refresh);

  const [editing, setEditing] = useState<Category | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  function openCreate() {
    setEditing(null);
    setFormError(null);
    setModalOpen(true);
  }

  function openEdit(category: Category) {
    setEditing(category);
    setFormError(null);
    setModalOpen(true);
  }

  async function handleSubmit(values: CategoryForm) {
    try {
      if (editing) {
        await update(editing.id, values);
        toast.success('Category updated.');
      } else {
        await create(values);
        toast.success(`"${values.name}" created.`);
      }
      // Expense rows carry a copy of the category name/icon, so refresh them.
      await refreshLedger();
      setModalOpen(false);
      setFormError(null);
    } catch (error) {
      setFormError(
        error instanceof DuplicateCategoryError
          ? error.message
          : (error as Error).message || 'Could not save the category.'
      );
    }
  }

  async function handleDelete(category: Category) {
    const count = await usage(category.id);
    const ok = await confirm({
      title: `Delete "${category.name}"?`,
      message:
        count > 0
          ? `${count} expense${count === 1 ? '' : 's'} use${
              count === 1 ? 's' : ''
            } this category. They'll be kept, but shown as uncategorised.`
          : 'This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    await remove(category.id);
    await refreshLedger();
    toast.success('Category deleted.');
  }

  return (
    <>
      <PageHeader
        title="Categories"
        description="Shared across every group — define whatever matches how you actually spend."
        actions={
          <Button icon={Plus} onClick={openCreate}>
            New category
          </Button>
        }
      />

      {categories.length === 0 ? (
        <Card padded={false}>
          <EmptyState
            icon={Tags}
            title="No categories"
            description="Categories are entirely yours to define."
            action={
              <Button icon={Plus} onClick={openCreate}>
                Create a category
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((category) => (
            <Card key={category.id} className="flex items-center gap-3 p-3.5">
              <CategoryGlyph icon={category.icon} color={category.color} />

              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{category.name}</span>
                {category.isDefault && (
                  <Badge className="mt-0.5">Built-in</Badge>
                )}
              </span>

              <span className="flex gap-0.5">
                <IconButton
                  icon={Pencil}
                  label={`Edit ${category.name}`}
                  size="sm"
                  onClick={() => openEdit(category)}
                />
                <IconButton
                  icon={Trash2}
                  label={`Delete ${category.name}`}
                  size="sm"
                  onClick={() => handleDelete(category)}
                />
              </span>
            </Card>
          ))}
        </div>
      )}

      <CategoryModal
        key={editing?.id ?? 'new'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        category={editing}
        error={formError}
        onSubmit={handleSubmit}
      />
    </>
  );
}

function CategoryModal({
  open,
  onClose,
  category,
  error,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  category: Category | null;
  error: string | null;
  onSubmit: (values: CategoryForm) => Promise<void>;
}) {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CategoryForm>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: category?.name ?? '',
      icon: category?.icon ?? CATEGORY_ICON_KEYS[0],
      color: category?.color ?? SWATCH_COLORS[0],
    },
  });

  const color = useWatch({ control, name: 'color' });
  const icon = useWatch({ control, name: 'icon' });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={category ? 'Edit category' : 'New category'}
      width="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={isSubmitting} onClick={handleSubmit(onSubmit)}>
            {category ? 'Save changes' : 'Create category'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-3 rounded-xl border border-line bg-elevated/50 p-3">
          <CategoryGlyph icon={icon} color={color} size="lg" />
          <span className="text-sm text-muted">Preview</span>
        </div>

        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input
              label="Name"
              placeholder="e.g. Coffee runs"
              error={errors.name?.message ?? error}
              autoComplete="off"
              {...field}
            />
          )}
        />

        <Controller
          control={control}
          name="color"
          render={({ field }) => (
            <ColorPicker label="Colour" value={field.value} onChange={field.onChange} />
          )}
        />

        <Controller
          control={control}
          name="icon"
          render={({ field }) => (
            <IconPicker label="Icon" value={field.value} onChange={field.onChange} tint={color} />
          )}
        />
      </div>
    </Modal>
  );
}
